import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormGroup, UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil, take } from "rxjs/operators";
import { select, Store } from "@ngrx/store";

import { PayCycleService } from "@shared/services/pay-cycle/pay-cycle.service";
import { MilestoneReport, Payout, Transaction } from "src/app/shared/models/payouts.types";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import {
	FundingDateMilestoneStatusOptions,
	IStatusForm,
	Milestone,
	MilestoneStatusesValue,
	MilestoneStatusOptions,
	MilestoneTypeName,
	MilestoneUpdate,
	MilestoneUpdateDTO,
	PayCycle
} from "@shared/models/pay-cycle.interface";
import { PayGroup } from "@shared/models/pay-groups";
import { MilestonesService } from "@shared/services/milestones/milestones.service";
import { TppPaymentsApiService } from "../../../../services/tpp-payments-api.service";
import { AppState } from "@store/models/state.model";
import { ResetStatusAction } from "@store/actions/statusNetPayment.action";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { UserRole } from "@shared/constants/roles";
import { getGlobalDashboardFilterState } from "@store/index";
import { GlobalDashboardFilterSelect } from "@store/models/globalDashboardFilterSelection.model";
import { UpdateDashboardFilterAction } from "@store/actions/globalDashboardFilterSelect.action";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import {
	FilterMilestoneTypes,
	ManualFlagStatus,
	MilestoneEventHistoryLogData
} from "@modules/global-dashboard/models/global-dashboard-interface";
import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import { timeZoneAbbreviation } from "@shared/utils/date.util";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";

@Component({
	selector: "app-calendar-events",
	templateUrl: "./calendar-events.component.html",
	styleUrls: ["./calendar-events.component.scss"]
})
export class CalendarEventsComponent implements OnInit, OnDestroy {
	@Input() set _payCycle(pay: PayCycle) {
		this.payCycle = pay;
	}

	@Input() set _payGroup(payGroup: PayGroup) {
		this.payGroup = payGroup;
	}

	@Input() set _milestones(value: Milestone[]) {
		this.milestones = value;
	}

	@Input() set _milestone(value: Milestone) {
		this.permissions
			.canEditFundingDateStatus()
			.pipe(take(1))
			.subscribe(res => {
				this.canEditFundingDateSatus = res;
				this.milestone = value;
				this.manualFlagStatus = this.milestone!.manualFlagStatus
					? this.milestone!.manualFlagStatus
					: ManualFlagStatus.NONE;
				this.hasBookingDate =
					value.extraInfo?.netBookingDate !== null &&
					value.extraInfo?.netBookingDate !== undefined &&
					value.type === "NETS";
				this.setupFundingSelectOption(value.type);
			});
	}

	showMilestonesState = false;

	@Input() set _showMilestonesState(val: boolean) {
		this.showMilestonesState = val;
	}

	@Input() fundingScreenEnabled = false;
	@Input() tppFundingScreenEnabled = false;

	@Input() events!: Observable<void>;

	@Output() updatePayCycle: EventEmitter<PayCycle> = new EventEmitter<PayCycle>();

	title = "Net payment";
	subtitle = "";
	statusColor: string | undefined = "";
	milestonesSelection!: SelectOption[];
	milestoneStatusForm!: FormGroup;
	payCycle!: PayCycle;
	payGroup!: PayGroup;
	milestone!: Milestone;
	milestones!: Milestone[];
	dataSource: BehaviorSubject<Payout[]> = new BehaviorSubject<Payout[]>([]);
	payoutTransactions!: Transaction[];
	milestoneReport!: MilestoneReport;
	eventsSubscription!: Subscription;
	payoutCurrency = "";
	hasBeenProcessed = false;
	componentDataLoading = true;
	showDropDown = false;
	hasBookingDate = false;
	manualFlagStatus = ManualFlagStatus.NONE;

	payouts$: Observable<Payout[]> = this.dataSource.asObservable();
	destroy$ = new Subject<void>();
	canEditFundingDateSatus = false;

	timeZoneShort = "";
	hasTimezoneFeature = false;

	private isDisabledField = false;
	private disabledFundingScreen = false;

	private router = inject(Router);
	private payCycleService = inject(PayCycleService);
	private milestonesService = inject(MilestonesService);
	private formBuilder = inject(UntypedFormBuilder);
	private tppPaymentsApi = inject(TppPaymentsApiService);
	private store = inject(Store<AppState>);
	private permissions = inject(PermissionsService);
	private historyLogService = inject(HistoryLogSidePanelService);
	private featureManagementService = inject(FeatureManagementService);

	constructor() {
		this.timeZoneShort = timeZoneAbbreviation();

		this.permissions
			.disableAccessToEditableFormField([UserRole.TS_IMPLEMENTATION_LEAD, UserRole.TS_VO, UserRole.CLIENT])
			.subscribe(isDisabled => (this.isDisabledField = isDisabled));

		this.featureManagementService
			.hasFeature(FeatureFlagCodes.Q3_TIMEZONE_FE)
			.pipe(take(1))
			.subscribe(hasFeature => (this.hasTimezoneFeature = hasFeature));
	}

	ngOnInit(): void {
		this.permissions
			.canEditFundingDateStatus()
			.pipe(take(1))
			.subscribe(res => {
				this.canEditFundingDateSatus = res;
			});

		this.eventsSubscription = this.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateMilestoneReport(this.milestone);
		});

		this.permissions
			.disableFundingScreen()
			.subscribe(disabledFundingScreen => (this.disabledFundingScreen = disabledFundingScreen));
	}

	ngOnDestroy(): void {
		this.dataSource.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
		this.eventsSubscription.unsubscribe();
	}

	onMilestoneClick(type: string) {
		this.handleMilestoneClickEvent(type);
		this.store.dispatch(new ResetStatusAction({ statusPayment: [] }));
	}

	private setupFundingSelectOption(milestoneType: MilestoneTypeName): void {
		const milestoneWithInProgressStatus = ["FUNDING_DATE", "NETS"];

		if (milestoneWithInProgressStatus.includes(milestoneType)) {
			this.milestonesSelection = FundingDateMilestoneStatusOptions;
		} else {
			this.milestonesSelection = MilestoneStatusOptions;
		}

		this.updateMilestonesStatus();
	}

	private updateMilestoneReport(milestone: Milestone) {
		this.componentDataLoading = true;

		if (milestone.type === "PAYMENT" || milestone.type === "NETS") {
			this.tppPaymentsApi
				.getMilestoneReport(milestone.id!)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (report: MilestoneReport) => {
						this.milestoneReport = this.updateReportCurrency(report);
						this.componentDataLoading = false;
					},
					error: () => {
						// Required to prevent infinite loading state on error
						this.componentDataLoading = false;
					}
				});
		} else {
			this.componentDataLoading = false;
		}
	}
	updateReportCurrency(report: MilestoneReport): MilestoneReport {
		const defaultCurrency = this.payGroup.data.currency;

		if (report.totalPayoutAmount.currency !== undefined && report.totalPayoutAmount.value === 0) {
			report.totalPayoutAmount.currency = defaultCurrency;
		}
		if (report.totalFailedAmount.currency !== undefined && report.totalFailedAmount.value === 0) {
			report.totalFailedAmount.currency = defaultCurrency;
		}
		if (report.totalFundingAmount.currency !== undefined && report.totalFundingAmount.value === 0) {
			report.totalFundingAmount.currency = defaultCurrency;
		}

		return report;
	}

	getTextOfMilestoneValue(statusValue?: string): string {
		return statusValue
			? FundingDateMilestoneStatusOptions.filter(option => option.value === statusValue)[0].text
			: "Not Found";
	}

	getValueOfMilestoneText(statusText: string): string {
		return FundingDateMilestoneStatusOptions.filter(option => option.text === statusText)[0].value;
	}

	toggleDropDown(show?: boolean): void {
		if (!this.canEditFundingDateSatus || this.isDisabledField) {
			this.showDropDown = false;
		} else {
			this.showDropDown = show !== undefined ? show : !this.showDropDown;
		}
	}

	setStatusSelection(status: string) {
		this.milestone.status = this.getTextOfMilestoneValue(status);
		this.milestoneStatusForm.get("milestoneStatus")?.patchValue({
			value: this.getTextOfMilestoneValue(status),
			disabled: !this.canEditFundingDateSatus || this.isDisabledField
		});
	}

	private updateMilestonesStatus() {
		if (this.milestone?.status === "UNDEFINED") {
			this.milestone.status = "PENDING";
		} else if (this.milestone?.status === "HAS_ERRORS") {
			this.milestone.status = "ERROR";
		} else if (this.milestone?.status === "COMPLETE") {
			this.milestone.status = "COMPLETE";
		} else if (this.milestone?.status === "IN_PROGRESS") {
			this.milestone.status = "IN_PROGRESS";
		}

		this.statusColor = this.milestone.status;

		this.initMilestoneStatusForm();
		this.updateMilestoneReport(this.milestone);
	}

	private initMilestoneStatusForm() {
		this.milestoneStatusForm = this.formBuilder.group({
			milestoneStatus: {
				value: this.getTextOfMilestoneValue(this.milestone.status),
				disabled: !this.canEditFundingDateSatus || this.isDisabledField
			}
		}) as IStatusForm;

		this.milestoneStatusForm
			.get("milestoneStatus")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
			.subscribe(status => {
				this.updateStatusMilestonePayCycle(this.getValueOfMilestoneText(status.value));
			});
	}

	private updateStatusMilestonePayCycle(status: string) {
		this.componentDataLoading = true;

		const milestoneUpdateDTO: MilestoneUpdateDTO = {
			milestones: []
		};

		const payDate = this.payCycleService.formatPayDateForMilestones(
			this.milestone.date,
			this.payGroup.legalEntity.data.timeZone
		);

		const milestoneToUpdate: MilestoneUpdate = {
			id: this.milestone.id,
			version: this.milestone.version,
			type: this.milestone.type,
			group: this.milestone.group,
			date: payDate,
			status: status,
			params: []
		};

		milestoneUpdateDTO.milestones.push(milestoneToUpdate);

		this.milestonesService.updateMilestones(milestoneUpdateDTO).subscribe({
			next: () => {
				this.updatePayCycle.emit(this.payCycle);
				this.componentDataLoading = false;
			},
			error: () => {
				// Required to prevent infinite loading state on error
				this.updatePayCycle.emit(this.payCycle);
				this.componentDataLoading = false;
			}
		});
	}

	private handleMilestoneClickEvent(type: string) {
		// dont redirect until component is ready as it injects data to dependants
		// assumes that any `@input()`'s to this component are resolved before this component is rendered in the view.
		if (this.componentDataLoading) return;

		let route = "payments";
		this.setSelectedKpi("dashboard");

		if (this.router.url === "/global-dashboard/dashboard") route = "global-dashboard";
		const g2nRoute =
			this.milestone.status === MilestoneStatusesValue.PENDING ||
			this.milestone.status === MilestoneStatusesValue.COMPLETE
				? `/${route}/payment-orders`
				: `/${route}/file-history`;

		switch (type) {
			case FilterMilestoneTypes.PAYMENTORDERS:
				this.router.navigate([g2nRoute], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycle.id,
						payCycleExternalId: this.payCycle.externalId,
						payCycle: this.payCycle,
						milestone: this.milestone,
						milestones: this.milestones,
						payrollCycleId: this.payCycle.payrollCycleId,
						previousUrl: this.router.url
					}
				});
				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;
			case FilterMilestoneTypes.PAYMENTS:
				this.router.navigate([`/${route}/payouts`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycle.id,
						payCycleName: this.payCycle.name,
						payCycle: this.payCycle,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.TPP_PAYMENTORDERS:
				this.router.navigate([`/${route}/tpp-data-input`], {
					state: {
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						milestones: this.milestones,
						milestone: this.milestone,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.TPP_PAYMENTS:
				this.router.navigate([`/${route}/tpp-payments`], {
					state: {
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: this.milestone,
						milestoneReport: this.milestoneReport,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.FUNDING:
				if (
					this.fundingScreenEnabled &&
					!this.disabledFundingScreen &&
					this.enabledFundingScreen(this.milestone.group)
				) {
					this.router.navigate([`/${route}/funding-date`], {
						state: {
							payGroup: this.payGroup,
							payCycle: this.payCycle,
							payCycleName: this.payCycle.name,
							milestones: this.milestones,
							milestone: this.milestone,
							milestoneReport: this.milestoneReport
						}
					});
					this.store.dispatch(
						new UpdateHistoryStateInfoAction({
							payGroup: this.payGroup,
							payCycle: this.payCycle,
							payCycleName: this.payCycle.name,
							milestones: this.milestones,
							milestone: this.milestone,
							milestoneReport: this.milestoneReport,
							payrollCycleId: this.payCycle.payrollCycleId
						})
					);
				}
				break;
		}
	}

	/**
	 * Check if funding screen is enabled for NETS or TPP
	 * @param group: milestone group
	 * @returns boolean
	 */
	enabledFundingScreen(group: string): boolean {
		if (group.includes("NETS") && this.fundingScreenEnabled) {
			return true;
		} else if (group.includes("TPP") && this.tppFundingScreenEnabled) {
			return true;
		} else {
			return false;
		}
	}
	/**
	 * Set up kpi to route user back in dashboard when user opens
	 * payment order / net payment page from dashboard
	 * @param kpi: Set current kpi when user is in dashboard
	 */
	setSelectedKpi(kpi: string): void {
		this.store.pipe(take(1), select(getGlobalDashboardFilterState)).subscribe({
			next: (state: GlobalDashboardFilterSelect) => {
				this.store.dispatch(
					new UpdateDashboardFilterAction({
						globalDashboard: {
							kpiSelected:
								state.globalDashboard.kpiSelected !== "" ? state.globalDashboard.kpiSelected : kpi,
							filters: state.globalDashboard.filters,
							isExpand: state.globalDashboard.isExpand
						}
					})
				);
			}
		});
	}

	openHistoryLog(): void {
		const milestoneEventHistoryLogData: MilestoneEventHistoryLogData = {
			milestoneId: this.milestone.id || "",
			milestoneType: this.milestone.type,
			customerExternalId: this.payGroup.customer.externalId,
			customerName: this.payGroup.customer.name,
			payGroupId: this.payGroup.id,
			paygroupName: this.payGroup.data.name,
			cycleName: this.payCycle.name,
			group: this.milestone.group,
			payCycleId: this.milestone.payCycleId
		};

		this.historyLogService.setMilestoneHistoryLogData(milestoneEventHistoryLogData);
	}
}
